<template>
  <div
    class="oring-stepper"
    :class="{ 'has-fixed-actionbar': showFixedActionBar }"
  >
    <Steps
      :steps="possibleSteps"
      :current-step="currentStep"
      :completed-steps="completedSteps"
      @go-to-step="goToStep($event)"
    />
    <div class="step-content">
      <div class="step-content__header">
        <h2>{{ currentStepTitle }}</h2>
        <ToggleSwitchSquare
          :disabled="globalLoading"
          v-if="currentStep === possibleSteps.length - 2 || currentStep === possibleSteps.length"
          :is-checked="store.oringCalculatorImperial"
          :label-right="i18n.tv('inch','ORingConfigurator')"
          :label-left="i18n.tv('mm','ORingConfigurator')"
          @change-value="toggleShowInchValues"
        />
      </div>
      <component
        ref="stepComponentRef"
        :is="currentStepComponent"
      />
      <div
        class="mobile-buttons-wrapper"
        :class="{ 'is-fixed': showFixedActionBar }"
      >
        <div class="step-content__buttons">
          <div class="left-buttons">
            <ReactiveButton
              v-if="currentStep !== 1"
              @click="goToPreviousStep"
            >
              {{ i18n.tv('Frontend.Wizard.PreviousButtonText', 'ORingConfiguratorImport') }}
            </ReactiveButton>
          </div>
          <div class="right-buttons">
            <ReactiveButton
              v-if="currentStep !== possibleSteps.length"
              @click="goToNextStep"
              :is-primary-button="false"
              type="submit"
              :button-text="i18n.tv('Frontend.Wizard.NextButtonText', 'ORingConfiguratorImport')"
              :disabled="!isCurrentStepValid"
            />
            <ReactiveButton
              v-if="currentStep===possibleSteps.length"
              @click="showStartPopup= true"
              :is-primary-button="true"
              :button-text="i18n.tv('startNewSearch', 'ORingConfigurator')"
            />
          </div>
        </div>
      </div>
    </div>

    <LeaveConfirmationPopup
      :show="showStartPopup"
      :size="POPUP.Size.small"
      :title="i18n.tv('startNewSearch', 'ORingConfigurator')"
      :content="i18n.tv('startNewSearchContent', 'ORingConfigurator')"
      :stay-button-text="i18n.tv('Frontend.Button.Cancel', 'ORingConfiguratorImport')"
      :leave-button-text="i18n.tv('startNewSearch', 'ORingConfigurator')"
      @update:show="showStartPopup = $event"
      @leave="startNewSearch"
    />
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch, onMounted, onBeforeUnmount} from 'vue'
import useI18nExtended from '~/composables/usei18nExtended'
import Steps from '~/components/input-components/Steps.vue'
import {Step} from '~/types/index.d.ts'
import {POPUP} from '~/assets/js/constants'
import ORingAssistantStep1 from '~/components/oring-calculator/ORingAssistantStep1.vue'
import ORingAssistantStep2 from '~/components/oring-calculator/ORingAssistantStep2.vue'
import ORingAssistantStep3 from '~/components/oring-calculator/ORingAssistantStep3.vue'
import ORingAssistantStep4 from '~/components/oring-calculator/ORingAssistantStep4.vue'
import ORingAssistantStep5 from '~/components/oring-calculator/ORingAssistantStep5.vue'
import {useORingCalculatorStore} from '@/store/oring-calculator'
import LeaveConfirmationPopup from '~/components/popup/LeaveConfirmationPopup.vue'
import ORingAssistantResults from '~/components/oring-calculator/ORingAssistantResults.vue'
import ToggleSwitchSquare from '~/components/input-components/ToggleSwitchSquare.vue'
import {storeToRefs} from 'pinia'
import {useOringScrollToTop} from '~/composables/oring/useOringScrollToTop'

const showStartPopup = ref(false)
const i18n = useI18nExtended()
const store = useORingCalculatorStore()
const {scrollToTop} = useOringScrollToTop()

const {globalLoading} = storeToRefs(store)

const steps: Omit<Step, 'number'>[] = [
  {title: i18n.tv('sealingCompressionAndMediaSelection', 'ORingConfigurator'), component: ORingAssistantStep1},
  {title: i18n.tv('movementAndGroove', 'ORingConfigurator'), component: ORingAssistantStep2},
  {title: i18n.tv('mountingPosition', 'ORingConfigurator'), component: ORingAssistantStep3},
  {title: i18n.tv('Frontend.Title.DesignSpace', 'ORingConfiguratorImport'), component: ORingAssistantStep4},
  {title: i18n.tv('Frontend.MaterialAssistant.Material', 'ORingConfiguratorImport'), component: ORingAssistantStep5},
  {title: i18n.tv('calculatedOringAndCatalogRing', 'ORingConfigurator'), component: ORingAssistantResults}
]

const possibleSteps = computed(() => {
  // Filter as before, but add number dynamically based on index
  const filtered = steps.filter((step, idx) =>
    (idx !== 1 && idx !== 2)
      || (idx === 1 && store.oringAssistantIsRadial)
      || (idx === 2 && store.oringAssistantIsRadial && !store.oringAssistantGrooveInShaft)
  )
  return filtered.map((step, idx) => ({...step, number: idx + 1}))
})

const currentStep = ref(1)
const completedSteps = ref<number[]>([])

onMounted(() => {
  currentStep.value = store.oringAssistantCurrentStep
  completedSteps.value = [...store.oringAssistantCompletedSteps]
})
// Define an interface for components with validate method
interface ComponentWithValidate {
  validate?: () => boolean;
  [key: string]: any;
}

const stepComponentRef = ref<ComponentWithValidate | null>(null)

const currentStepTitle = computed(() => {
  return possibleSteps.value.find(step => step.number === currentStep.value)?.title || ''
})
const currentStepComponent = computed(() => {
  return possibleSteps.value.find(step => step.number === currentStep.value)?.component || null
})

// Check if the current step is valid
const isCurrentStepValid = computed(() => {
  // If there's no step component reference, assume it's valid
  if (!stepComponentRef.value) {return true}

  // If the step component has a validate method, call it
  if (stepComponentRef.value.validate) {
    return stepComponentRef.value.validate()
  }

  // If there's no validate method, assume the step is valid
  return true
})

const goToStep = async (number: number) => {
  completedSteps.value = possibleSteps.value
    .filter(step => step.number < number)
    .map(step => step.number)
  currentStep.value = number
  scrollToTop('auto')
}

const goToNextStep = async () => {
  // Only proceed if the current step is valid
  if (!isCurrentStepValid.value) {
    return
  }

  if (!completedSteps.value.includes(currentStep.value)) {
    completedSteps.value.push(currentStep.value)
  }
  if (currentStep.value < possibleSteps.value.length) {
    currentStep.value += 1
    scrollToTop('auto')
  }
}

const goToPreviousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value -= 1
    scrollToTop('auto')
  }
}

const toggleShowInchValues = (value: boolean) => {
  store.performUnitLengthSwitch(value)
}

const startNewSearch = () => {
  store.resetAssistantStates()
  currentStep.value = 1
  completedSteps.value = []
  showStartPopup.value = false
  store.resetNavigationState()
  scrollToTop('auto')
}

// Track scroll to decide when to show fixed action bar (mobile)
const scrolledY = ref(0)
const onScroll = () => {
  scrolledY.value = window.scrollY || document.documentElement.scrollTop || 0
}

const showFixedActionBar = computed(() => {
  return currentStep.value === possibleSteps.value.length && scrolledY.value >= 600
})

onMounted(() => {
  window.addEventListener('scroll', onScroll, {passive: true})
  onScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
})

watch(possibleSteps, (newSteps) => {
  const validNumbers = newSteps.map(step => step.number)
  // Only keep completed steps that are still sequentially before the current step and exist in possibleSteps
  completedSteps.value = completedSteps.value
    .filter(num => validNumbers.includes(num) && num < currentStep.value)
  // If currentStep is not in possibleSteps, reset to 1
  if (!validNumbers.includes(currentStep.value)) {
    currentStep.value = 1
  }
})

// Watch for changes in navigation state to automatically save to store
watch([currentStep, completedSteps], () => {
  store.oringAssistantCurrentStep = currentStep.value
  store.oringAssistantCompletedSteps = [...completedSteps.value]
}, {deep: true})
</script>

<style lang="scss">
.oring-stepper {
  // Add bottom spacing only when mobile fixed action bar is visible
  &.has-fixed-actionbar {
    @include breakpoint-down($md) {
      padding-bottom: 7rem; // approx. bar height + spacing
    }
  }

  .step-content {
    &__header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 1rem;

       h2 {
        margin: 0;
      }
    }

    &__buttons {
      display: flex;
      justify-content: space-between;

      .left-buttons {
        display: flex;
        flex: 1;

        .k-button.k-button-solid-primary {
          background-color: $color__fst__blue__010;
          color: $color__fst__primary;
        }
      }

      .right-buttons {
        display: flex;
        justify-content: flex-end;
        flex: 1;
      }
    }
  }
}

@include breakpoint-down($sm) {
  .oring-stepper {
    .step-content {
      &__header {
       display: flex;
        flex-direction: column;
        gap: 1.25rem;

        .toggle-switch-square {
          width: 8.5rem;
        }
      }

      &__buttons {
        gap: 1rem;
        width: 100%;
      }
    }

    // Mobile fixed action bar wrapper
    .mobile-buttons-wrapper {
      // Default: stay in normal flow on mobile
      position: static;

      &.is-fixed {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        box-sizing: border-box;
        background: $color__white;
        padding: 1rem;
        padding-bottom: calc(1rem + env(safe-area-inset-bottom));
        /* stylelint-disable-next-line */
        box-shadow: 0 -.1rem 0.25rem rgba(0, 0, 0, 16%);
        z-index: 100;
      }
    }
  }
}
</style>
